// Pickups — "ништяки" that drop from killed guards.
//
// Design note (the flavor is borrowed, the numbers are ours):
// caravan escorts in other games carry telling loot, and КОРОВАНЫ leans on
// that vocabulary so a drop reads instantly.
//   - Fallout caravans haul chems + bottle caps. Their chems ARE a
//     temporary-buff system: Psycho (+damage), Turbo/Jet (faster actions),
//     Med-X / Buffout (damage resistance), and Stimpak (instant heal). We
//     reskin those as Боевой клич / Быстрая рука / Кураж / Аптечка (non-drug
//     names on purpose: drug references pushed the Play IARC rating to 16+).
//   - Bottle caps (Fallout) and denars (Mount & Blade) → a coin purse.
//   - Mount & Blade / Battle Brothers caravans move trade goods (spice,
//     silk, salt) → the rare "мешок специй" gold jackpot.
//
// A Pickup is a world entity shaped like Loot (scatter on spawn, bob, magnet
// to the player, collect on touch). Collecting one dispatches by `kind`
// (see Game._collectPickup / Simulator): heal and buff mutate the player,
// coin and jackpot award gold. All randomness flows through the optional
// seeded rng so the headless simulator reproduces runs byte-for-byte.

import { Vec2, CONST } from './utils.js';
import { randFn } from './rng.js';

// Timed buffs the player can carry. Magnitudes + durations live in CONST;
// Player._recomputeBuffs() reads them. Kept as plain strings so player.js
// and ui.js can reference them without importing extra symbols.
export const BuffType = {
  RAGE: 'rage',     // +damage          (Fallout Psycho)
  FRENZY: 'frenzy', // −attack cooldown (Fallout Turbo/Jet)
  GUARD: 'guard',   // damage resistance (Fallout Med-X/Buffout)
};

// How a collected pickup is applied.
export const PickupKind = {
  HEAL: 'heal',
  COIN: 'coin',
  BUFF: 'buff',
  JACKPOT: 'jackpot',
  MOUNT: 'mount',
};

// Drop table. `weight` is the relative spawn weight once the drop roll
// succeeds. The medkit weight is computed dynamically in rollGuardDrop
// (anti-frustration: common when the player is hurt, rare at full HP), so
// its static weight here is only a floor.
export const PICKUP_TYPES = [
  { id: 'medkit', kind: PickupKind.HEAL,    icon: '❤', color: '#ff6b6b', weight: 6,  labelKey: 'pickup.medkit' },
  { id: 'purse',  kind: PickupKind.COIN,    icon: '🪙', color: '#ffd700', weight: 34, labelKey: 'pickup.purse' },
  { id: 'rage',   kind: PickupKind.BUFF,    icon: '💢', color: '#ff4d4d', weight: 12, buff: BuffType.RAGE,   labelKey: 'pickup.rage' },
  { id: 'frenzy', kind: PickupKind.BUFF,    icon: '⚡', color: '#ffd24d', weight: 12, buff: BuffType.FRENZY, labelKey: 'pickup.frenzy' },
  { id: 'guard',  kind: PickupKind.BUFF,    icon: '🛡', color: '#6bc5ff', weight: 10, buff: BuffType.GUARD,  labelKey: 'pickup.guard' },
  { id: 'spice',  kind: PickupKind.JACKPOT, icon: '🌶', color: '#e0843a', weight: 4,  labelKey: 'pickup.spice' },
  // Rare: only from CONST.HORSE_MIN_WAVE and never while already riding
  // (see dropWeight).
  { id: 'horse',  kind: PickupKind.MOUNT,   icon: '🐎', color: '#c08a4a', weight: CONST.PICKUP_HORSE_WEIGHT, labelKey: 'pickup.horse' },
];

// Gold pickups (purse, spice) are part of the wave's loot: after the wave is
// cleared they stream home with the coins, and the wave waits for them.
export function isGoldPickup(pickup) {
  return pickup.alive && (pickup.kind === PickupKind.COIN || pickup.kind === PickupKind.JACKPOT);
}

export function getPickupType(id) {
  return PICKUP_TYPES.find(t => t.id === id) || null;
}

// Icon/color per buff for the HUD effect row. Derived from the buff
// entries in PICKUP_TYPES so the visuals stay defined in one place.
export const BUFF_DISPLAY = PICKUP_TYPES
  .filter(t => t.kind === PickupKind.BUFF)
  .reduce((m, t) => { m[t.buff] = { icon: t.icon, color: t.color }; return m; }, {});

// Seconds a given buff lasts. Reads CONST so durations stay centralized.
export function buffDuration(buff) {
  switch (buff) {
    case BuffType.RAGE: return CONST.BUFF_RAGE_DURATION;
    case BuffType.FRENZY: return CONST.BUFF_FRENZY_DURATION;
    case BuffType.GUARD: return CONST.BUFF_GUARD_DURATION;
    default: return 0;
  }
}

export class Pickup {
  constructor(x, y, type, wave = 1, rng = null) {
    this.pos = new Vec2(x, y);
    this.type = type;
    this.kind = type.kind;
    this.radius = CONST.PICKUP_RADIUS;
    this.alive = true;

    const rand = randFn(rng);

    // Gold value is baked at spawn for coin/jackpot so it's deterministic
    // and the floating "+N" shown on pickup matches what was awarded.
    if (type.kind === PickupKind.COIN) {
      const span = CONST.PICKUP_COIN_MAX - CONST.PICKUP_COIN_MIN + 1;
      this.value = CONST.PICKUP_COIN_MIN + Math.floor(rand() * span);
    } else if (type.kind === PickupKind.JACKPOT) {
      this.value = CONST.PICKUP_SPICE_GOLD_BASE + Math.max(0, wave) * CONST.PICKUP_SPICE_GOLD_PER_WAVE;
    } else {
      this.value = 0;
    }

    // Pop out of the corpse like a coin.
    const angle = rand() * Math.PI * 2;
    const speed = 40 + rand() * 50;
    this.vel = new Vec2(Math.cos(angle) * speed, Math.sin(angle) * speed);
    this.friction = 5;
    this.bobTimer = rand() * Math.PI * 2;
  }

  // Mirrors Loot.update. Returns the pickup `type` when collected this
  // frame (truthy for the caller to dispatch on), null otherwise.
  update(dt, playerPos, worldWidth, worldHeight, magnetRangeMul = 1, magnetSpeedMul = 1) {
    if (!this.alive) return null;

    if (this.vel.lenSq() > 1) {
      const frictionMul = Math.exp(-this.friction * dt);
      this.vel = this.vel.mul(frictionMul);
      this.pos = this.pos.add(this.vel.mul(dt));

      const margin = this.radius;
      if (this.pos.x < margin) this.pos = new Vec2(margin, this.pos.y);
      if (this.pos.x > worldWidth - margin) this.pos = new Vec2(worldWidth - margin, this.pos.y);
      if (this.pos.y < margin) this.pos = new Vec2(this.pos.x, margin);
      if (this.pos.y > worldHeight - margin) this.pos = new Vec2(this.pos.x, worldHeight - margin);
    }

    this.bobTimer += dt * 3;

    const distToPlayer = this.pos.dist(playerPos);
    const magnetRange = CONST.PICKUP_MAGNET_RANGE * magnetRangeMul;
    if (distToPlayer < magnetRange) {
      const dir = playerPos.sub(this.pos).normalize();
      this.pos = this.pos.add(dir.mul(CONST.PICKUP_MAGNET_SPEED * magnetSpeedMul * dt));
    }

    if (distToPlayer < this.radius + CONST.PLAYER_RADIUS) {
      this.alive = false;
      return this.type;
    }

    return null;
  }

  render(renderer) {
    if (!this.alive) return;
    const bobY = Math.sin(this.bobTimer) * 2;
    const x = this.pos.x;
    const y = this.pos.y + bobY;
    // Dark token + colored ring so the drop pops against the sand, with the
    // emoji glyph on top (glyphs carry their own colors).
    renderer.circle(x, y, this.radius + 3, 'rgba(0,0,0,0.55)');
    renderer.strokeCircle(x, y, this.radius + 3, this.type.color, 2);
    renderer.textThin(this.type.icon, x, y + 0.5, '#fff', 13, 'center', 'middle');
  }
}

// Roll a drop for one killed guard. Returns a Pickup or null. Consumes the
// rng even when nothing drops (one chance roll), so seeded sim streams stay
// aligned. Anti-frustration: the medkit weight climbs as the player's HP
// fraction falls — in the spirit of game.js's W4 mercy heal.
export function rollGuardDrop(guard, player, wave = 1, rng = null) {
  const rand = randFn(rng);
  if (rand() >= CONST.GUARD_DROP_CHANCE) return null;

  const hpFrac = player && player.maxHp > 0
    ? Math.max(0, Math.min(1, player.hp / player.maxHp))
    : 1;
  // medkit weight: base (6) at full HP → ~50 near death.
  const medkitWeight = 6 + (1 - hpFrac) * 44;
  const horseAllowed = wave >= CONST.HORSE_MIN_WAVE && !(player && player.horse);
  const dropWeight = (tp) => {
    if (tp.id === 'medkit') return medkitWeight;
    if (tp.kind === PickupKind.MOUNT) return horseAllowed ? tp.weight : 0;
    return tp.weight;
  };

  let total = 0;
  for (const tp of PICKUP_TYPES) total += dropWeight(tp);

  let roll = rand() * total;
  let chosen = null;
  for (const tp of PICKUP_TYPES) {
    const w = dropWeight(tp);
    if (roll < w) { chosen = tp; break; }
    roll -= w;
  }
  // Float leftovers: fall back to the last type that can drop.
  if (!chosen) chosen = PICKUP_TYPES.filter(tp => dropWeight(tp) > 0).pop();

  return new Pickup(guard.pos.x, guard.pos.y, chosen, wave, rng);
}

// Apply a collected pickup to the player. Player-only effects (heal, buff)
// are mutated here; gold-bearing kinds are returned for the caller to route
// through its own score/telemetry plumbing. Returns a small descriptor:
//   heal           → { kind:'heal', amount }     amount = HP actually restored
//   buff           → { kind:'buff', buff, duration }
//   coin / jackpot → { kind, gold }
//   mount          → { kind:'mount', mounted }  false = already riding, no-op
export function applyPickup(type, player, value = 0) {
  switch (type.kind) {
    case PickupKind.HEAL: {
      const amount = player.heal(CONST.PICKUP_HEAL_AMOUNT, 'medkit');
      return { kind: PickupKind.HEAL, amount };
    }
    case PickupKind.BUFF: {
      const duration = buffDuration(type.buff);
      player.applyBuff(type.buff, duration);
      return { kind: PickupKind.BUFF, buff: type.buff, duration };
    }
    case PickupKind.COIN:
    case PickupKind.JACKPOT:
      return { kind: type.kind, gold: value };
    case PickupKind.MOUNT:
      return { kind: PickupKind.MOUNT, mounted: player.mountHorse() };
    default:
      return { kind: type.kind };
  }
}
